import { ZERO_MONEY, applyPercent, compare, isNegative, percent, } from '@aeris/money';
/**
 * O desconto do documento, como a loja o PENSA — F100.
 *
 * ## Por que um tipo, e não só um número
 *
 * A loja pensa "10% pra ele". O sistema só sabia guardar "R$ 12,34", e as duas
 * coisas divergem no instante em que alguém acrescenta um item: o valor
 * continua o mesmo e os dez por cento viram outra coisa, sem ninguém decidir.
 *
 * Guardar a INTENÇÃO é o que permite reabrir a venda dizendo "10%" e reajustar
 * o valor quando as linhas mudam. Rederivar o percentual a partir do valor não
 * serve: `12,34 / 123,33` numa nota de R$ 123,33 dá 10,0057%, e mostrar isso é
 * confessar que o sistema não sabe o que a loja pediu.
 *
 * ## O que continua sendo a verdade
 *
 * `discount_amount`, em reais. É ele que vai para a NF-e, é ele que é rateado
 * pelas linhas, e é a soma das linhas que tem de bater com o total (a razão
 * inteira está no topo de `totals.ts`). O percentual é a intenção; o valor é o
 * fato.
 *
 * ## Um lugar só
 *
 * `@aeris/sales` é importado pelo web, pela API e pelo Edge. A derivação mora
 * aqui porque é o único ponto em que os três não podem divergir — a mesma razão
 * que pôs `computeTotals` neste arquivo vizinho.
 */
/** O maior percentual aceitável. Cem por cento é dar a mercadoria, e é legítimo. */
export const MAX_DISCOUNT_PERCENT = percent('100');
export const amountDiscount = (amount) => ({
    kind: 'amount',
    amount,
});
export const percentDiscount = (rate) => ({
    kind: 'percent',
    rate,
});
/** Nenhum desconto. */
export const NO_DISCOUNT = amountDiscount(ZERO_MONEY);
/**
 * Um `Decimal` cru continua valendo como desconto em reais.
 *
 * Todos os chamadores de `computeTotals` passavam um `Decimal`, e continuam
 * podendo: a assinatura aceita os dois e esta função é onde a diferença morre.
 * Sem isto, acrescentar o percentual teria mudado a assinatura de sete lugares
 * que não têm nada a ver com percentual nenhum.
 */
export const asHeaderDiscount = (input) => ('kind' in input ? input : amountDiscount(input));
/**
 * O valor em reais que a intenção significa, para ESTA base.
 *
 * A base é o líquido — o subtotal menos os descontos que já estão nas linhas —
 * e não o bruto. Três consequências, todas desejadas:
 *
 * 1. o resultado nunca passa do que sobrou, então a constraint
 *    `discount_amount <= subtotal_amount` do banco não pode ser violada nem a
 *    cem por cento;
 * 2. é a mesma base que o rateio já usa como peso (`allocate` em `totals.ts`),
 *    então o desconto de cabeçalho não pesa duas vezes sobre uma linha que já
 *    tinha desconto próprio;
 * 3. "cem por cento" zera a nota de verdade. Sobre o bruto, com um desconto de
 *    linha presente, cem por cento excederia o líquido e seria RECUSADO — um
 *    "tudo" que o sistema não aceita.
 *
 * O arredondamento é o de `applyPercent`: half-up, uma única redução a
 * centavos. 10% de R$ 33,33 é R$ 3,33; de R$ 33,35 é R$ 3,34.
 */
export const resolveDiscount = (intent, base) => {
    if (intent.kind === 'amount') {
        return isNegative(intent.amount)
            ? { ok: false, failure: { reason: 'negative-discount' } }
            : { ok: true, amount: intent.amount };
    }
    if (isNegative(intent.rate) ||
        compare(intent.rate, MAX_DISCOUNT_PERCENT) > 0) {
        return { ok: false, failure: { reason: 'percent-out-of-range' } };
    }
    return { ok: true, amount: applyPercent(base, intent.rate) };
};
/**
 * O percentual a GUARDAR junto do documento, ou nulo.
 *
 * Nulo quando o desconto foi digitado em reais — e isso é informação, não
 * ausência dela: significa "esta nota tem um valor de desconto, não uma
 * política de desconto". Reabrir mostra reais, e acrescentar um item não mexe
 * nele.
 */
export const percentToStore = (intent) => intent.kind === 'percent' ? intent.rate : null;
/** Só para quem lê um documento gravado: o que veio do banco vira intenção. */
export const storedDiscount = (input) => input.percent === null
    ? amountDiscount(input.amount)
    : percentDiscount(input.percent);
//# sourceMappingURL=discount.js.map